import type { IntegrationSetting } from '../integration/entity';
import type { DataOutIntegrationWriteInput } from './entity';

/**
 * Pure helpers shared by the data-out config page and the write path.
 */

/**
 * The blank settings map for a connector that has never been configured, keyed
 * off the catalog's settings schema: booleans start `false`, everything else
 * starts as an empty string.
 *
 * Note this ignores each setting's `default`. That field is the *required* flag
 * (`null` means required, `false` means optional) rather than an initial value,
 * and the backend fills real defaults itself for any key the save omits.
 */
export function defaultDataOutSettings(settings: readonly IntegrationSetting[] | undefined) {
  const defaults: Record<string, string | boolean> = {};
  for (const setting of settings ?? []) {
    defaults[setting.key] = setting.type === 'boolean' ? false : '';
  }
  return defaults;
}

/** Whether a setting must be filled in before the connector can be saved. */
export function isDataOutSettingRequired(setting: IntegrationSetting): boolean {
  return setting.default === null;
}

/**
 * The `enabled` value a save actually stores.
 *
 * A connector with no events selected is always disabled — the backend enforces
 * that too, so sending anything else would just be overwritten. On first
 * connect there is no enable toggle on the form, so selecting events is what
 * turns the connector on; afterwards the toggle decides, and having no events
 * still wins.
 */
export function deriveDataOutEnabled({
  isNew,
  enabled,
  events,
}: {
  isNew: boolean;
  enabled: boolean;
  events: readonly string[];
}): boolean {
  const hasEventsSelected = events.length > 0;
  return isNew ? hasEventsSelected : hasEventsSelected && enabled;
}

/** Apply {@link deriveDataOutEnabled} to a write payload. */
export function withDerivedEnabled(input: DataOutIntegrationWriteInput, isNew: boolean): DataOutIntegrationWriteInput {
  return {
    ...input,
    enabled: deriveDataOutEnabled({ isNew, enabled: input.enabled, events: input.events }),
  };
}
